import React, { Component } from "react"
import { getHumanTime, getMilitaryTime, getSimplifiedMilitaryTime, getTimeInfo, getTimeInfoForACT } from "../../services/utils/date-util"
import "./index.css"

interface Props {
    fromInSeconds: number
    countdownForModule: string
    warningThreshold?: number
    onTimerTick?: (remainingSeconds) => void
    onFinish?: () => void
}

interface State {
    remainingSeconds: number
    warning: boolean
    showTimerInfo: boolean
}

const DEFAULT_WARNING_THRESHOLD = 5*60 // 5 mins

export default class CountdownTimer extends Component<Props, State> {
    timerId
    endTime

    constructor(props: Props) {
        super(props)
        this.state = {
            remainingSeconds: props.fromInSeconds,
            warning: false,
            showTimerInfo: true,
        }

    }

    initialize = () => {
        clearInterval(this.timerId)
        let {fromInSeconds} = this.props
        let now = Date.now()
        this.endTime = now + fromInSeconds * 1000

        if (fromInSeconds <= 0) {
            return
        }

        this.timerId = setInterval(() => {
            let warning = false
            let {warningThreshold=DEFAULT_WARNING_THRESHOLD, onFinish, onTimerTick} = this.props
            let now = Date.now()
            let remainingSeconds = Math.max(Math.ceil((this.endTime - now) / 1000), 0)

            if (onTimerTick) {
                onTimerTick(remainingSeconds)
            }

            if (remainingSeconds <= warningThreshold) {
                warning = true
            }

            if (remainingSeconds <= 0) {
                clearInterval(this.timerId)

                if (onFinish) {
                    onFinish()
                }
            }

            this.setState({remainingSeconds, warning})
        }, 500)
    }

    componentDidMount() {
        this.initialize()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if (prevProps.countdownForModule !== this.props.countdownForModule || prevProps.fromInSeconds !== this.props.fromInSeconds) {
            let remainingSeconds = this.props.fromInSeconds

            if (isNaN(remainingSeconds)) {
                remainingSeconds = 100
            }

            this.setState({remainingSeconds})
            this.initialize()
        }
    }

    componentWillUnmount(): void {
        clearInterval(this.timerId)
    }

    toggleTimer = () => {
        let {showTimerInfo} = this.state
        this.setState({showTimerInfo: !showTimerInfo})
    }

    render() {
        let {remainingSeconds, showTimerInfo, warning} = this.state
        let timerInfo = getSimplifiedMilitaryTime(remainingSeconds).text
        let infoClassName = `coundown-timer-info ${warning ? "warning": ""}`

        showTimerInfo = warning ? true: showTimerInfo

        return (
            <div className="countdown-timer">
                <div className="info-container">
                    { showTimerInfo ?
                        <div className={infoClassName}>{timerInfo}</div>
                        :
                        <div className="countdowntimer-icon" data-src="/assets/images/icons/timer.svg">
                            <svg version="1.1" x="0px" y="0px" viewBox="0 0 33 41">
                                <path style={{fill: '#1395d7'}} d="M29.9,14.6l1.8-1.8c0.3-0.4,0.3-1,0-1.4l-1.3-1.3c-0.4-0.4-0.9-0.4-1.3,0c0,0,0,0,0,0l-1.7,1.6
                                    c-2.5-2.2-5.7-3.6-9-4V3.8h2.2c0.5,0,0.9-0.4,1-0.9V1c0-0.5-0.4-1-1-1h-8.3c-0.5,0-1,0.5-1,1v1.9c0,0.5,0.4,0.9,1,0.9h2.2v3.9
                                    C5.5,8.8-0.9,17,0.1,26.1s9.2,15.5,18.3,14.4c9-1.1,15.5-9.2,14.4-18.3C32.5,19.5,31.5,16.9,29.9,14.6z M16.5,36.8
                                    c-7,0-12.7-5.7-12.7-12.7c0-7,5.7-12.7,12.7-12.7c7,0,12.7,5.7,12.7,12.7c0,0,0,0,0,0C29.2,31.2,23.5,36.8,16.5,36.8z M17.5,28h-1.9
                                    c-0.6,0-1-0.4-1-1V16.2c0-0.5,0.5-1,1-1h1.9c0.5,0,0.9,0.4,1,1V27C18.4,27.5,17.9,27.9,17.5,28L17.5,28z"
                                />
                            </svg>
                        </div>
                    }
                </div>

                <div className="control d-none d-md-block">
                    {!warning &&
                        <button onClick={this.toggleTimer}>
                            { showTimerInfo ? 
                                <div>Hide</div>
                                :
                                <div>Show</div>
                            }
                        </button>
                    }
                </div>
            </div>
        )
    }
}

interface Props2 {
    fromInSeconds: number
    onTimerTick?: (remainingSeconds) => void
    onFinish?: () => void
}

interface State2 {
    remainingSeconds: number
}

/**
 * Unlike the default countdown timer, this timer doesn't provide any styling on purpose.
 * It simply displays the timer info which could be styled later
 */
export class CountdownTimer2 extends Component<Props2, State2> {
    timerId
    endTime

    constructor(props: Props) {
        super(props)
        this.state = {
            remainingSeconds: props.fromInSeconds,
        }
    }

    componentDidMount(): void {
        this.initialize()
    }

    componentWillUnmount(): void {
        clearInterval(this.timerId)
    }

    initialize = () => {
        clearInterval(this.timerId)
        let {fromInSeconds} = this.props
        let now = Date.now()
        this.endTime = now + fromInSeconds * 1000

        if (fromInSeconds <= 0) {
            return
        }

        this.timerId = setInterval(() => {
            let {onFinish, onTimerTick} = this.props
            let now = Date.now()
            let remainingSeconds = Math.max(Math.ceil((this.endTime - now) / 1000), 0)

            if (onTimerTick) {
                onTimerTick(remainingSeconds)
            }

            if (remainingSeconds <= 0) {
                clearInterval(this.timerId)

                if (onFinish) {
                    onFinish()
                }
            }

            this.setState({remainingSeconds})
        }, 1000)
    }

    render() {
        let {remainingSeconds} = this.state
        let timerInfo = getSimplifiedMilitaryTime(remainingSeconds).text

        return (
            <>{timerInfo}</>
        )
    }
}


interface Props3 extends Props2 {
    showTime?: boolean
    startTimer: boolean
}

// Simplified Countdown timer for Digital ACT tests
export class CountdownTimerACT extends Component<Props3, State2> {
    timerId
    endTime

    constructor(props: Props3) {
        super(props)
        this.state = {
            remainingSeconds: props.fromInSeconds,
        }
    }

    componentDidMount(): void {
        this.initialize()
    }

    componentWillUnmount(): void {
        clearInterval(this.timerId)
    }

    componentDidUpdate(prevProps: Readonly<Props3>, prevState: Readonly<State2>, snapshot?: any): void {
        if (this.props.fromInSeconds !== prevProps.fromInSeconds || this.props.startTimer !== prevProps.startTimer) {
            this.initialize()
        }
    }

    initialize = () => {
        clearInterval(this.timerId)
        let {fromInSeconds} = this.props
        let now = Date.now()
        this.endTime = now + fromInSeconds * 1000

        if (fromInSeconds <= 0) {
            return
        }

        this.timerId = setInterval(() => {
            let {startTimer, onFinish, onTimerTick} = this.props
            let now = Date.now()
            let remainingSeconds = Math.max(Math.ceil((this.endTime - now) / 1000), 0)

            // Is the timer started?
            if (!startTimer) {
                return
            }

            if (onTimerTick) {
                onTimerTick(remainingSeconds)
            }

            if (remainingSeconds <= 0) {
                clearInterval(this.timerId)

                if (onFinish) {
                    onFinish()
                }
            }

            this.setState({remainingSeconds})
        }, 100)
    }

    render() {
        let {showTime=true, fromInSeconds, startTimer} = this.props
        let {remainingSeconds} = this.state
        let blankInfo = "-- : -- : --"

        // Initial value
        if (!startTimer) remainingSeconds = fromInSeconds

        let timerInfo = remainingSeconds !== -1 ? getTimeInfoForACT(remainingSeconds): blankInfo

         return (
            showTime ?
                <>
                    {timerInfo}
                </>
                :
                blankInfo
        )
    }
}
